import ProductEolImpact from "@/components/drawers/ProductDrawer/ProductEolImpact";
import ProductionImpactPerProduct from "@/components/drawers/ProductDrawer/ProductionImpactPerProduct";
import ProductUsageImpact from "@/components/drawers/ProductDrawer/ProductUsageImpact";
import TransportImpactPerProduct from "@/components/drawers/ProductDrawer/TransportImpactPerProduct";
import useTranslation from "@/hooks/useTranslation";
import { ProductBy } from "@/pages/dashboard/hooks/useProductImpact";
import { ImpactMetric } from "@/pages/dashboard/utils/types";
import { Tabs } from "antd";
import { useSearchParams } from "react-router-dom";
import MaterialImpactPerMaterial from "./MaterialImpactPerMaterial";
import { PRODUCT_DRAWER_URL_TAB } from "./ProductDrawer";
import styles from "./ProductDrawer.module.scss";
import PackagingImpactPerProduct from "@/components/drawers/ProductDrawer/PackagingImpactPerProduct/PackagingImpactPerProduct";

export type ProductDrawerTabKey =
  | "material"
  | "production"
  | "transport"
  | "use_phase"
  | "end_of_life";

export default function ProductDrawerContent({
  productId,
  metric,
  by,
}: {
  productId: number;
  metric: ImpactMetric;
  by: ProductBy;
}) {
  const tGeneral = useTranslation().general;
  const tTopContributors = useTranslation().productDrawer.topContributors;

  const tabs = [
    {
      label: tGeneral.lifecyclePhases.material,
      key: "material",
      children: (
        <MaterialImpactPerMaterial
          productId={productId}
          metric={metric}
          by={by}
        />
      ),
    },
    {
      label: tGeneral.lifecyclePhases.packaging,
      key: "packaging",
      children: (
        <PackagingImpactPerProduct
          productId={productId}
          metric={metric}
          by={by}
        />
      ),
    },
    {
      label: tGeneral.lifecyclePhases.production,
      key: "production",
      children: (
        <ProductionImpactPerProduct
          metric={metric}
          productId={productId}
          by={by}
        />
      ),
    },
    {
      label: tGeneral.lifecyclePhases.transport,
      key: "transport",
      children: (
        <TransportImpactPerProduct
          productId={productId}
          metric={metric}
          by={by}
        />
      ),
    },
    {
      label: tGeneral.lifecyclePhases.usePhase,
      key: "use_phase",
      children: (
        <ProductUsageImpact
          productId={productId}
          metric={metric}
          by={by}
        />
      ),
    },
    {
      label: tGeneral.lifecyclePhases.endOfLife,
      key: "end_of_life",
      children: (
        <ProductEolImpact
          productId={productId}
          metric={metric}
          by={by}
        />
      ),
    },
  ];

  const extractTabKey = (key: string | null): ProductDrawerTabKey => {
    if (tabs.find((x) => x.key === key)) {
      return key as ProductDrawerTabKey;
    }
    return "material";
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTabKey = extractTabKey(searchParams.get(PRODUCT_DRAWER_URL_TAB));

  const switchTab = (key: string) => {
    setSearchParams((searchParams) => {
      searchParams.set(PRODUCT_DRAWER_URL_TAB, key);
      return searchParams;
    });
  };

  return (
    <>
      <span className={styles["title"]}>{tTopContributors.title}</span>
      <p className={styles["description"]}>{tTopContributors.description}</p>
      <Tabs
        items={tabs}
        activeKey={activeTabKey}
        onChange={switchTab}
      />
    </>
  );
}
